import React, { useState, Fragment } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Seniority } from '@/hooks/useSeniorities';

interface SeniorityComboboxProps {
    selectedId: string | undefined;
    onChange: (seniority: Seniority | null) => void;
    disabled?: boolean;
    seniorities: Seniority[];
    loading: boolean;
    error: string | null;
}

const SeniorityCombobox: React.FC<SeniorityComboboxProps> = ({ selectedId, onChange, disabled, seniorities, loading, error }) => {
    const [query, setQuery] = useState('');

    const selectedSeniority = seniorities.find(s => s.id === selectedId);

    const filteredSeniorities =
        query === ''
            ? seniorities
            : seniorities.filter((seniority) =>
                seniority.name
                    .toLowerCase()
                    .replace(/\s+/g, '')
                    .includes(query.toLowerCase().replace(/\s+/g, ''))
            );

    if (loading) return <div className="input-primary w-full mt-1 text-gray-500">Carregando senioridades...</div>;
    if (error) return <div className="input-primary w-full mt-1 text-red-500">{error}</div>;

    return (
        <Combobox value={selectedSeniority || null} onChange={onChange} disabled={disabled}>
            <div className="relative mt-1">
                <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm border border-gray-300">
                    <Combobox.Input
                        className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                        displayValue={(seniority: Seniority) => seniority?.name || ''}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Selecione uma senioridade"
                    />
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                        />
                    </Combobox.Button>
                </div>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery('')}
                >
                    <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-10">
                        {filteredSeniorities.length === 0 && query !== '' ? (
                            <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                                Nenhuma senioridade encontrada.
                            </div>
                        ) : (
                            filteredSeniorities.map((seniority) => (
                                <Combobox.Option
                                    key={seniority.id}
                                    className={({ active }) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                        }`
                                    }
                                    value={seniority}
                                >
                                    {({ selected, active }) => (
                                        <>
                                            <span
                                                className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                                                    }`}
                                            >
                                                {seniority.name}
                                            </span>
                                            {selected ? (
                                                <span
                                                    className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'
                                                        }`}
                                                >
                                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                </Combobox.Option>
                            ))
                        )}
                    </Combobox.Options>
                </Transition>
            </div>
        </Combobox>
    );
};

export default SeniorityCombobox; 